/**
 * Form component for managing multiple phone numbers with FHIR ContactPoint support.
 * @agentic @benchmark/phone
 */

// Components
import {
  Stack,
  Group,
  Select,
  NumberInput,
  Button,
  ActionIcon,
  Text,
} from "@mantine/core";
import { PhoneInput } from "@benchmark/ui/components/atomic";
import { IconTrash, IconPlus } from "@tabler/icons-react";

// Hooks
import { useCallback, useMemo, useState } from "react";

/**
 * Single phone number entry with value, use type, and rank.
 * @agentic @benchmark/phone
 */
export type PhoneNumberEntry = {
  value: string;
  use: "home" | "work" | "temp" | "mobile";
  rank: number;
  id?: string; // Stable identifier for tracking entries
};

/**
 * Props for PhoneNumbersEditor component.
 * @agentic @benchmark/phone
 */
export type PhoneNumbersEditorProps = {
  value: PhoneNumberEntry[];
  onChange: (value: PhoneNumberEntry[]) => void;
  error?: string;
  required?: boolean;
};

/**
 * FHIR ContactPoint use options for phone type selection.
 * @agentic @benchmark/phone
 */
const PHONE_USE_OPTIONS = [
  { value: "mobile", label: "Mobile" },
  { value: "home", label: "Home" },
  { value: "work", label: "Work" },
  { value: "temp", label: "Temporary" },
];

/**
 * Renders add/remove phone number rows with type and rank.
 * @agentic @benchmark/phone
 */
export const PhoneNumbersEditor = ({
  value = [],
  onChange,
  error,
  required = true,
}: PhoneNumbersEditorProps) => {
  // Generate deterministic stable IDs based on array index
  // This ensures each position in the array has a consistent ID
  // Use useState to avoid accessing ref.current during render
  const [stableIdPrefix] = useState(() => crypto.randomUUID());

  const phonesWithIds = useMemo(() => {
    return value.map((phone, index) => {
      // If phone already has an ID, use it
      if (phone.id) {
        return phone as PhoneNumberEntry & { id: string };
      }

      // Generate stable ID based on position and component instance
      // This ensures the ID is stable across re-renders for the same position
      const stableId = `${stableIdPrefix}-${index}`;
      return { ...phone, id: stableId };
    });
  }, [value, stableIdPrefix]);

  // Sort phones by rank for display
  const sortedPhones = useMemo(() => {
    return [...phonesWithIds].sort((a, b) => a.rank - b.rank);
  }, [phonesWithIds]);

  // Add a new phone number entry
  const handleAdd = useCallback(() => {
    // Find the next available rank
    const maxRank =
      phonesWithIds.length > 0
        ? Math.max(...phonesWithIds.map((p) => p.rank))
        : 0;

    const newPhone: PhoneNumberEntry = {
      value: "",
      use: "mobile",
      rank: maxRank + 1,
      id: crypto.randomUUID(),
    };

    onChange([...phonesWithIds, newPhone]);
  }, [phonesWithIds, onChange]);

  // Remove a phone number entry by ID
  const handleRemove = useCallback(
    (id: string) => {
      const newValue = phonesWithIds.filter((phone) => phone.id !== id);
      onChange(newValue);
    },
    [phonesWithIds, onChange],
  );

  // Update a specific field of a phone entry by ID
  const handleUpdate = useCallback(
    (
      id: string,
      field: keyof PhoneNumberEntry,
      fieldValue: string | number,
    ) => {
      const newValue = phonesWithIds.map((phone) => {
        if (phone.id === id) {
          return { ...phone, [field]: fieldValue };
        }
        return phone;
      });
      onChange(newValue);
    },
    [phonesWithIds, onChange],
  );

  return (
    <Stack gap="sm">
      {sortedPhones.length === 0 && (
        <Text size="sm" c="dimmed">
          No phone numbers added yet. Click "Add Phone Number" to get started.
        </Text>
      )}

      {sortedPhones.map((phone) => {
        // Parse error message to determine if this specific phone has an error
        // Error format: "Phone number rank X: ..." or "Phone number #X: ..."
        let phoneError: string | undefined = undefined;
        if (error) {
          const rankMatch = error.match(/rank\s+(\d+)/i);
          const indexMatch = error.match(/#(\d+)/);

          if (rankMatch && phone.rank === parseInt(rankMatch[1], 10)) {
            // Extract the error message after the colon
            const errorParts = error.split(":");
            phoneError =
              errorParts.length > 1
                ? errorParts.slice(1).join(":").trim()
                : error;
          } else if (indexMatch) {
            // For index-based errors, we need to match by position in sorted array
            const errorIndex = parseInt(indexMatch[1], 10);
            const phoneIndex =
              sortedPhones.findIndex((p) => p.id === phone.id) + 1;
            if (phoneIndex === errorIndex) {
              const errorParts = error.split(":");
              phoneError =
                errorParts.length > 1
                  ? errorParts.slice(1).join(":").trim()
                  : error;
            }
          } else if (!phone.value && error.includes("cannot be empty")) {
            // Fallback for empty value errors
            phoneError = "Required";
          }
        }

        return (
          <Group key={phone.id} align="flex-start" gap="xs" wrap="nowrap">
            {/* Phone Number Input */}
            <PhoneInput
              placeholder="(555) 123-4567"
              value={phone.value}
              onChange={(e) => handleUpdate(phone.id!, "value", e.target.value)}
              error={phoneError}
              style={{ flex: 2, minWidth: 150 }}
            />

            {/* Use Type Select */}
            <Select
              placeholder="Type"
              data={PHONE_USE_OPTIONS}
              value={phone.use}
              onChange={(val) =>
                handleUpdate(phone.id!, "use", val || "mobile")
              }
              style={{ flex: 1, minWidth: 100 }}
              aria-label="Phone type"
            />

            {/* Rank Input */}
            <NumberInput
              placeholder="Rank"
              value={phone.rank}
              onChange={(val) => handleUpdate(phone.id!, "rank", val || 1)}
              min={1}
              step={1}
              style={{ width: 80 }}
              aria-label="Priority rank"
            />

            {/* Remove Button */}
            <ActionIcon
              color="red"
              variant="light"
              onClick={() => handleRemove(phone.id!)}
              disabled={required && phonesWithIds.length === 1}
              aria-label="Remove phone number"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        );
      })}

      {/* Add Button */}
      <Button
        leftSection={<IconPlus size={16} />}
        variant="light"
        onClick={handleAdd}
        fullWidth
      >
        Add Phone Number
      </Button>

      {/* Error Message - only show if not already shown on individual fields */}
      {error &&
        !sortedPhones.some((phone) => {
          const rankMatch = error.match(/rank\s+(\d+)/i);
          const indexMatch = error.match(/#(\d+)/);
          if (rankMatch && phone.rank === parseInt(rankMatch[1], 10))
            return true;
          if (indexMatch) {
            const errorIndex = parseInt(indexMatch[1], 10);
            const phoneIndex =
              sortedPhones.findIndex((p) => p.id === phone.id) + 1;
            if (phoneIndex === errorIndex) return true;
          }
          return false;
        }) && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}
    </Stack>
  );
};
